/**
 * components/viewer/InspectorBridge.jsx — Element Inspector (Iter 31 P1-B).
 *
 * Tap a structure → inspector slides quietly from the right. Drawing stays
 * dominant. Calm + operational. NEVER blocks orientation. Press Esc or tap
 * the close button to dismiss.
 *
 * Mount: `<InspectorProvider>` wraps SheetViewer; `<InspectorBridge />` is
 * rendered as a child. Producers (LayerOverlay tap) call
 * `useInspector().open({ kind:'structure', id, payload: structure })`.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import StructureInspector from "@/components/object/StructureInspector";
import AlignmentInspector from "@/components/object/AlignmentInspector";
import PipeInspector from "@/components/object/PipeInspector";
import OperationalConflictInspector from "@/components/object/ConflictInspector";

const InspectorCtx = createContext(null);

export function InspectorProvider({ children }) {
  const [state, setState] = useState(null);

  const open  = useCallback((target) => setState(target || null), []);
  const close = useCallback(() => setState(null), []);

  // Esc dismisses.
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, close]);

  const value = useMemo(() => ({ state, open, close }), [state, open, close]);
  return <InspectorCtx.Provider value={value}>{children}</InspectorCtx.Provider>;
}

export function useInspector() {
  return useContext(InspectorCtx) || NO_OP;
}

const NO_OP = Object.freeze({ state: null, open: () => {}, close: () => {} });


/**
 * Default-export — the inspector panel itself. Renders only when the
 * context has state. Slides in from the right with a 180ms ease-out
 * translateX so the camera stays calm — no modal flash, no dim overlay.
 */
export default function InspectorBridge({ review = null, myAck = null, onAckResolved = null, distanceFromMe = null, sheetId = null, gpsContext = null }) {
  const { state, close } = useInspector();
  // Render but slide off-screen when state is null — lets the
  // CSS transition handle dismissal smoothly.
  const visible = !!state;
  const isStructure = state?.kind === "structure";
  const isAlignment = state?.kind === "alignment";
  const isPipe = state?.kind === "pipe";
  const isConflict = state?.kind === "conflict";
  const s = state?.payload;
  return (
    <div
      className="fixed top-16 bottom-3 right-3 z-30 w-80 max-w-[92vw] transition-transform duration-200 ease-out pointer-events-none"
      style={{ transform: visible ? "translateX(0)" : "translateX(110%)" }}
      data-testid="inspector-bridge"
    >
      {isStructure && (
        <div
          className="bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md shadow-2xl pointer-events-auto h-full overflow-y-auto flex flex-col"
          data-testid="inspector-panel"
          data-inspector-kind="structure"
          data-structure-id={s?.id}
        >
          <StructureInspector
            structure={s}
            onClose={close}
            review={review}
            myAck={myAck}
            onAckResolved={onAckResolved}
            distanceFromMe={distanceFromMe}
            sheetId={sheetId}
            gpsContext={gpsContext}
          />
        </div>
      )}
      {isAlignment && (
        <div
          className="bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md shadow-2xl pointer-events-auto h-full overflow-hidden flex flex-col"
          data-testid="inspector-panel"
          data-inspector-kind="alignment"
          data-alignment-id={s?.id}
        >
          <AlignmentInspector alignment={s} onClose={close} />
        </div>
      )}
      {isPipe && (
        <div
          className="bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md shadow-2xl pointer-events-auto h-full overflow-hidden flex flex-col"
          data-testid="inspector-panel"
          data-inspector-kind="pipe"
          data-pipe-id={s?.id}
        >
          <PipeInspector pipe={s} onClose={close} />
        </div>
      )}
      {isConflict && (
        <div
          className="bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md shadow-2xl pointer-events-auto h-full overflow-hidden flex flex-col"
          data-testid="inspector-panel"
          data-inspector-kind="conflict"
          data-conflict-id={s?.id}
        >
          <OperationalConflictInspector conflict={s} onClose={close} />
        </div>
      )}
    </div>
  );
}
